/**
 * Strategy Research page — covered-call screener + LEAP hunting.
 *
 * Scans a watchlist of symbols, ranks them by premium yield vs assignment
 * risk, and lets the user launch a bot from the results. The LEAP mode is
 * a MANUAL-trading tool: it screens 5–10% OTM calls at ~18M and ~24M
 * tenors across a curated thematic universe, scored on IV-vs-realized-vol,
 * theta bleed, leverage, premium cost, and liquidity — no bot, no orders.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { Analytics, ContentCopy, PlayArrow, Search } from '@mui/icons-material';
import WatchlistEditor from './WatchlistEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

interface Candidate {
  symbol: string;
  currentPrice: number;
  shareCostPer100?: number;
  maxContracts: number;
  totalShareCost?: number;
  totalCashSecured?: number;
  // Iron Condor fields
  putBuyStrike?: number;
  putSellStrike?: number;
  callSellStrike?: number;
  callBuyStrike?: number;
  putCredit?: number;
  callCredit?: number;
  netCredit?: number;
  maxLoss?: number;
  totalMaxLoss?: number;
  returnOnRisk?: number;
  probProfit?: number;
  strategy?: string;
  strike: number;
  expiration: string;
  bid: number;
  ask: number;
  mid: number;
  impliedVolatility: number;
  delta: number;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
  premiumPerContract: number;
  totalPremium: number;
  premiumYieldWeekly: number;
  premiumYieldAnnual: number;
  volume: number;
  openInterest: number;
  score: number;
  dte: number;
  // LEAP fields
  sector?: string;
  tenor?: string;
  otmPct?: number;
  occSymbol?: string;
  contractsMin?: number;
  contractsMax?: number;
  premiumPctOfSpot?: number;
  realizedVol?: number | null;
  ivRvRatio?: number | null;
  thetaPctDay?: number | null;
  leverage?: number | null;
  breakevenMovePct?: number;
  movePctForPlus50?: number | null;
  strikeDistInVols90?: number | null;
  pTouch90Pct?: number | null;
  spreadPct?: number;
}

type SortField = keyof Candidate;
type SortDir = 'asc' | 'desc';

const fmt = (n: number | null | undefined, digits = 2): string =>
  n == null || Number.isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtPct = (n: number | null | undefined): string =>
  n == null ? '—' : `${n >= 0 ? '' : ''}${fmt(n)}%`;

const Research: React.FC = () => {
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState('COVERED_CALL');
  const [budget, setBudget] = useState<number>(50000);
  const [deltaRange, setDeltaRange] = useState<[number, number]>([0.15, 0.30]);
  const [watchlist, setWatchlist] = useState<string[]>([]);
  // LEAP mode
  const [leapSectors, setLeapSectors] = useState<Record<string, string[]>>({});
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  // User-added LEAP symbols (screened as "Custom"); survives reloads.
  const [customSymbols, setCustomSymbols] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('leapCustomSymbols') || '[]'); }
    catch { return []; }
  });
  const [customInput, setCustomInput] = useState('');
  const addCustomSymbols = (raw: string) => {
    const parts = raw.split(/[\s,]+/).map((s) => s.trim().toUpperCase())
      .filter((s) => s && s.length <= 6 && /^[A-Z]+$/.test(s));
    if (!parts.length) { setCustomInput(''); return; }
    setCustomSymbols((prev) => {
      const next = Array.from(new Set([...prev, ...parts]));
      try { localStorage.setItem('leapCustomSymbols', JSON.stringify(next)); } catch {}
      return next;
    });
    setCustomInput('');
  };
  const [otmRange, setOtmRange] = useState<[number, number]>([5, 10]);
  const [ticketMin, setTicketMin] = useState<number>(15000);
  const [leapExps, setLeapExps] = useState<{ date: string; dte: number }[]>([]);
  const [selectedExps, setSelectedExps] = useState<string[]>([]);
  const [leapExpsLoading, setLeapExpsLoading] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanned, setScanned] = useState(false);
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Load watchlist + account buying power on mount
  useEffect(() => {
    (async () => {
      try {
        const [wlRes, acctRes, luRes] = await Promise.all([
          fetch(`${API_BASE}/api/research/watchlist`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/trade/portfolio`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/research/leap-universe`, { headers: authHeaders() }),
        ]);
        const wlData = await wlRes.json();
        if (wlData.success) setWatchlist(wlData.watchlist || []);
        const luData = await luRes.json().catch(() => null);
        if (luData?.success && luData.sectors) {
          setLeapSectors(luData.sectors);
          setSelectedSectors(Object.keys(luData.sectors));
        }

        // Use Alpaca account buying power if available
        const acctData = await acctRes.json();
        if (acctData.success && acctData.portfolio?.summary?.totalValue) {
          // Use a reasonable portion of portfolio value as budget default
        }
      } catch (err) {
        console.error('Init error:', err);
      }
    })();
  }, []);

  // Real listed LEAP dates (standardized across names) — loaded when the
  // LEAP mode is first opened; defaults pre-select ~18M and ~24M.
  useEffect(() => {
    if (strategy !== 'LEAP' || leapExps.length > 0 || leapExpsLoading) return;
    (async () => {
      setLeapExpsLoading(true);
      try {
        const r = await fetch(`${API_BASE}/api/research/leap-expirations`, {
          headers: authHeaders(),
        });
        const j = await r.json();
        if (j.success && Array.isArray(j.expirations) && j.expirations.length) {
          setLeapExps(j.expirations);
          const closest = (target: number) =>
            [...j.expirations].sort(
              (a: any, b: any) => Math.abs(a.dte - target) - Math.abs(b.dte - target)
            )[0]?.date;
          setSelectedExps(
            Array.from(new Set([closest(548), closest(730)].filter(Boolean)))
          );
        }
      } catch (err) {
        console.error('LEAP expirations load error:', err);
      } finally {
        setLeapExpsLoading(false);
      }
    })();
  }, [strategy, leapExps.length, leapExpsLoading]);

  const handleScan = async () => {
    setLoading(true);
    setError('');
    setCandidates([]);
    setScanned(false);
    try {
      const r = await fetch(`${API_BASE}/api/research/screen`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(
          strategy === 'LEAP'
            ? {
                strategy,
                budget, // max ticket
                budgetMin: ticketMin,
                otmMin: otmRange[0],
                otmMax: otmRange[1],
                sectors: selectedSectors,
                ...(selectedExps.length ? { expirations: selectedExps } : {}),
                ...(customSymbols.length ? { extraSymbols: customSymbols } : {}),
              }
            : {
                strategy,
                budget,
                watchlist,
                deltaMin: deltaRange[0],
                deltaMax: deltaRange[1],
              }
        ),
      });
      const j = await r.json();
      if (!r.ok || !j.success) {
        throw new Error(j.detail || j.message || 'Scan failed');
      }
      if (strategy === 'LEAP' && j.jobId) {
        // The LEAP scan is throttled to protect the live bot's broker
        // budget and takes minutes for a full universe — longer than
        // the proxy's request ceiling (the "<!DOCTYPE" JSON error,
        // 9/16). The server hands back a job; poll it to completion.
        const deadline = Date.now() + 6 * 60 * 1000;
        for (;;) {
          await new Promise((res) => setTimeout(res, 2500));
          if (Date.now() > deadline) throw new Error('Scan timed out — try fewer sectors');
          const pr = await fetch(`${API_BASE}/api/research/screen-job/${j.jobId}`, {
            headers: authHeaders(),
          });
          const pj = await pr.json();
          if (!pr.ok || !pj.success) throw new Error(pj.detail || 'Scan polling failed');
          if (pj.status === 'error') throw new Error(pj.error || 'Scan failed');
          if (pj.status === 'done') {
            setCandidates(pj.candidates || []);
            break;
          }
        }
      } else {
        setCandidates(j.candidates || []);
      }
      setScanned(true);
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sorted = [...candidates].sort((a, b) => {
    const va = (a as any)[sortField] ?? 0;
    const vb = (b as any)[sortField] ?? 0;
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : va < vb ? 1 : -1;
  });

  const handleSaveWatchlist = async (symbols: string[]) => {
    setWatchlist(symbols);
    try {
      await fetch(`${API_BASE}/api/research/watchlist`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ watchlist: symbols }),
      });
    } catch (err) {
      console.error('Save watchlist error:', err);
    }
  };

  const SortHeader: React.FC<{
    field: SortField; label: string; align?: 'left' | 'right'; tip?: string;
  }> = ({ field, label, align = 'right', tip }) => (
    <TableCell align={align} sx={{ fontWeight: 600 }}>
      <Tooltip title={tip || ''} arrow placement="top" enterDelay={200}>
        <TableSortLabel
          active={sortField === field}
          direction={sortField === field ? sortDir : 'desc'}
          onClick={() => handleSort(field)}
          sx={tip ? { textDecorationLine: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 } : undefined}
        >
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Analytics sx={{ fontSize: 36, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Strategy Research
        </Typography>
      </Box>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Strategy</InputLabel>
                <Select value={strategy} label="Strategy" onChange={(e) => setStrategy(e.target.value)}>
                  <MenuItem value="COVERED_CALL">Covered Call</MenuItem>
                  <MenuItem value="WHEEL">Wheel (Cash-Secured Put)</MenuItem>
                  <MenuItem value="IRON_CONDOR">Iron Condor</MenuItem>
                  <MenuItem value="LEAP">LEAP Calls (manual)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {strategy === 'LEAP' && (
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Ticket min ($)"
                  value={ticketMin}
                  onChange={(e) => setTicketMin(Math.max(0, parseFloat(e.target.value) || 0))}
                  inputProps={{ min: 0, step: 1000 }}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={strategy === 'LEAP' ? 'Ticket max ($)' : 'Budget ($)'}
                value={budget}
                onChange={(e) => setBudget(Math.max(0, parseFloat(e.target.value) || 0))}
                inputProps={{ min: 0, step: 1000 }}
              />
            </Grid>

            {strategy === 'LEAP' ? (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  OTM range: {otmRange[0].toFixed(1)}% – {otmRange[1].toFixed(1)}%
                </Typography>
                <Slider
                  value={otmRange}
                  onChange={(_, v) => setOtmRange(v as [number, number])}
                  min={2}
                  max={20}
                  step={0.5}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Delta range: {deltaRange[0].toFixed(2)} – {deltaRange[1].toFixed(2)}
                </Typography>
                <Slider
                  value={deltaRange}
                  onChange={(_, v) => setDeltaRange(v as [number, number])}
                  min={0.05}
                  max={0.50}
                  step={0.01}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleScan}
                disabled={
                  loading ||
                  (strategy === 'LEAP'
                    ? selectedSectors.length === 0 ||
                      (leapExps.length > 0 && selectedExps.length === 0)
                    : watchlist.length === 0)
                }
                startIcon={loading ? <CircularProgress size={18} /> : <Search />}
              >
                {loading ? 'Scanning...' : 'Scan'}
              </Button>
            </Grid>
          </Grid>

          {strategy === 'LEAP' ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Universe ({selectedSectors.reduce((n, s) => n + (leapSectors[s]?.length || 0), 0)}{' '}
                symbols) — OTM calls at the expirations picked below, scored on IV vs realized
                vol, theta bleed, leverage, premium cost, and liquidity. Manual tool: nothing
                is traded from here.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Expirations:
                </Typography>
                {leapExpsLoading && <CircularProgress size={14} />}
                {leapExps.map((e) => (
                  <Chip
                    key={e.date}
                    label={`${e.date} · ${Math.round(e.dte / 30.4)}mo`}
                    size="small"
                    color={selectedExps.includes(e.date) ? 'primary' : 'default'}
                    variant={selectedExps.includes(e.date) ? 'filled' : 'outlined'}
                    onClick={() =>
                      setSelectedExps((prev) =>
                        prev.includes(e.date)
                          ? prev.filter((d) => d !== e.date)
                          : [...prev, e.date]
                      )
                    }
                  />
                ))}
                {!leapExpsLoading && leapExps.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    (couldn't load listed dates — the scan will use ~18M/~24M automatically)
                  </Typography>
                )}
              </Box>
              <FormGroup row sx={{ mt: 0.5 }}>
                {Object.entries(leapSectors).map(([sector, syms]) => (
                  <FormControlLabel
                    key={sector}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedSectors.includes(sector)}
                        onChange={(_, on) =>
                          setSelectedSectors((prev) =>
                            on ? [...prev, sector] : prev.filter((s) => s !== sector)
                          )
                        }
                      />
                    }
                    label={`${sector} (${syms.length})`}
                  />
                ))}
              </FormGroup>
              {/* User-added names beyond the curated universe — screened
                  as the "Custom" sector; persisted locally. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Add symbols"
                  placeholder="e.g. MRVL, ALAB"
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addCustomSymbols(customInput);
                    }
                  }}
                  onBlur={() => addCustomSymbols(customInput)}
                  sx={{ width: { xs: "100%", sm: 220 } }}
                />
                {customSymbols.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    onDelete={() =>
                      setCustomSymbols((prev) => {
                        const next = prev.filter((x) => x !== s);
                        try { localStorage.setItem('leapCustomSymbols', JSON.stringify(next)); } catch {}
                        return next;
                      })
                    }
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <WatchlistEditor symbols={watchlist} onChange={handleSaveWatchlist} />
            </Box>
          )}
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {strategy === 'LEAP'
              ? `Scanning ${selectedSectors.reduce(
                  (n, s) => n + (leapSectors[s]?.length || 0),
                  0
                )} symbols across ${selectedExps.length || 2} expiries — rate-limited to protect the live bot, ~1–2 minutes.`
              : `Scanning ${watchlist.length} symbols... This may take 10–20 seconds.`}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {scanned && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Candidates ({candidates.length})
              </Typography>
              {strategy !== 'LEAP' && candidates.length > 0 && candidates[0].expiration && (
                <Chip
                  label={`Target: ${candidates[0].expiration} (${candidates[0].dte} DTE)`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            {candidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No candidates found. Try adjusting your budget, delta range, or watchlist.
              </Typography>
            ) : strategy === 'LEAP' ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <SortHeader field="symbol" label="Symbol" align="left"
                        tip="Underlying name and its universe sector. Custom = a symbol you added beyond the curated list." />
                      <SortHeader field="tenor" label="Tenor" align="left"
                        tip="Expiration bucket/date. LEAPs live ~18-24 months out: long enough for the thesis to play, short enough that theta is already priced sanely." />
                      <SortHeader field="currentPrice" label="Price"
                        tip="Current underlying price — the reference for strike, OTM % and breakeven." />
                      <SortHeader field="strike" label="Strike"
                        tip="The call strike being screened. Above the current price by the OTM % you set." />
                      <SortHeader field="otmPct" label="OTM %"
                        tip="How far the strike sits above spot. More OTM = cheaper ticket and more leverage, but the stock must travel further before expiry." />
                      <SortHeader field="mid" label="Mid"
                        tip="Option mid price per share (bid/ask midpoint) — the fair-ish value between what buyers bid and sellers ask." />
                      <SortHeader field="premiumPerContract" label="$/Contract"
                        tip="Cost of ONE contract (mid × 100) — the actual cash ticket, and the maximum you can lose." />
                      <SortHeader field="contractsMax" label="Fits Ticket"
                        tip="How many contracts fit inside your budget range. 0 = this candidate is priced out of the ticket." />
                      <SortHeader field="impliedVolatility" label="IV"
                        tip="Implied volatility — the price of time in this option. Lower = cheaper convexity; the sleeve study only ever bought cheap-IV windows (IV rank < 20)." />
                      <SortHeader field="ivRvRatio" label="IV/RV"
                        tip="Implied vs realized volatility. Above 1: you pay more than the stock has actually been moving. Below 1: the option is cheap relative to real movement. Lower is better; 30% of the score." />
                      <SortHeader field="delta" label="Delta"
                        tip="Share-equivalent exposure: 0.60 delta ≈ 60 shares per contract. Also a rough market-implied probability of finishing in the money." />
                      <SortHeader field="thetaPctDay" label="Θ %/day"
                        tip="Daily time decay as % of the premium — the rent you pay to hold. Lower is better; 10% of the score." />
                      <SortHeader field="leverage" label="Lev."
                        tip="Notional stock exposure per premium dollar (delta × spot ÷ mid). Higher = more torque per dollar; 15% of the score." />
                      <SortHeader field="breakevenMovePct" label="B/E move"
                        tip="How far the stock must RISE by expiry for the position to break even at settlement (strike + premium vs spot). You can still profit earlier on a smaller move — this is the hold-to-expiry hurdle." />
                      <SortHeader field="movePctForPlus50" label="+50% move"
                        tip="Spot move for the OPTION price to gain +50% at constant IV (delta/gamma estimate). The pre-expiry companion to B/E move: how far before this position is meaningfully green, if the move comes reasonably fast." />
                      <SortHeader field="pTouch90Pct" label="P(touch) 90d"
                        tip="Approximate probability the stock TOUCHES the strike within ~90 days, from realized vol (how the stock actually moves, not what options cost). Higher = the strike is plausibly reached soon. An approximation and a ranking aid, not a quote." />
                      <SortHeader field="spreadPct" label="Sprd %"
                        tip="Bid-ask spread as % of mid — your round-trip execution cost. Wide spreads silently eat the edge; execution quality is first-class here. Biggest share of the liquidity score." />
                      <SortHeader field="openInterest" label="OI"
                        tip="Open interest — how many contracts exist at this strike/date. Depth for getting out at a fair price later." />
                      <SortHeader field="score" label="Score"
                        tip="Composite 0-100 percentile rank within the tenor: 30% cheap IV/RV + 10% low theta + 10% close strike in realized-vol terms + 15% leverage + 15% low premium-vs-spot + 20% liquidity (spread & OI). A ranking aid, not a signal — your thesis still decides." />
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        OCC
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((c) => (
                      <TableRow key={`${c.symbol}-${c.tenor}`} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {c.symbol}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.sector}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${c.expiration} · ${c.dte}d`}
                            size="small"
                            variant="outlined"
                            color={c.dte >= 640 ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">${fmt(c.currentPrice)}</TableCell>
                        <TableCell align="right">${fmt(c.strike, 0)}</TableCell>
                        <TableCell align="right">{fmt(c.otmPct, 1)}%</TableCell>
                        <TableCell align="right">${fmt(c.mid)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${fmt(c.premiumPerContract, 0)}
                        </TableCell>
                        <TableCell align="right">
                          {c.contractsMin === c.contractsMax
                            ? `${c.contractsMax}x`
                            : `${c.contractsMin}–${c.contractsMax}x`}
                        </TableCell>
                        <TableCell align="right">{fmtPct(c.impliedVolatility)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color:
                              c.ivRvRatio == null
                                ? 'text.secondary'
                                : c.ivRvRatio <= 1.0
                                  ? 'success.main'
                                  : c.ivRvRatio >= 1.5
                                    ? 'error.main'
                                    : 'text.primary',
                          }}
                        >
                          {fmt(c.ivRvRatio)}
                        </TableCell>
                        <TableCell align="right">{fmt(c.delta, 3)}</TableCell>
                        <TableCell align="right">{fmt(c.thetaPctDay, 3)}</TableCell>
                        <TableCell align="right">{fmt(c.leverage, 1)}</TableCell>
                        <TableCell align="right">{fmt(c.breakevenMovePct, 1)}%</TableCell>
                        <TableCell align="right">{fmt(c.movePctForPlus50, 1)}%</TableCell>
                        <TableCell align="right">{fmt(c.pTouch90Pct, 0)}%</TableCell>
                        <TableCell align="right">{fmt(c.spreadPct, 1)}</TableCell>
                        <TableCell align="right">{c.openInterest ?? '—'}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={fmt(c.score, 0)}
                            size="small"
                            color={c.score >= 70 ? 'success' : c.score >= 45 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={`Copy ${c.occSymbol || ''}`}>
                            <Button
                              size="small"
                              startIcon={<ContentCopy fontSize="small" />}
                              onClick={() =>
                                c.occSymbol && navigator.clipboard?.writeText(c.occSymbol)
                              }
                            >
                              Copy
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <SortHeader field="symbol" label="Symbol" align="left" />
                      <SortHeader field="currentPrice" label="Price" />
                      <SortHeader field="maxContracts" label="Contracts" />
                      {strategy === 'IRON_CONDOR' ? (
                        <>
                          <SortHeader field="putSellStrike" label="Put Spread" />
                          <SortHeader field="callSellStrike" label="Call Spread" />
                          <SortHeader field="netCredit" label="Net Credit" />
                          <SortHeader field="totalPremium" label="Total Prem." />
                          <SortHeader field="maxLoss" label="Max Loss" />
                          <SortHeader field="returnOnRisk" label="ROI %" />
                          <SortHeader field="probProfit" label="Win %" />
                        </>
                      ) : (
                        <>
                          <SortHeader field="totalShareCost" label="Total Cost" />
                          <SortHeader field="strike" label="Strike" />
                          <SortHeader field="mid" label="Premium" />
                          <SortHeader field="totalPremium" label="Total Prem." />
                          <SortHeader field="premiumYieldWeekly" label="Yield/Wk" />
                          <SortHeader field="premiumYieldAnnual" label="Yield/Yr" />
                        </>
                      )}
                      <SortHeader field="impliedVolatility" label="IV" />
                      <SortHeader field="delta" label="Delta" />
                      <SortHeader field="score" label="Score" />
                      <TableCell sx={{ fontWeight: 600 }} align="center">
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sorted.map((c) => (
                      <TableRow key={c.symbol} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {c.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">${fmt(c.currentPrice)}</TableCell>
                        <TableCell align="right">{c.maxContracts}</TableCell>
                        {strategy === 'IRON_CONDOR' ? (
                          <>
                            <TableCell align="right">
                              ${fmt(c.putBuyStrike)}/${fmt(c.putSellStrike)}
                            </TableCell>
                            <TableCell align="right">
                              ${fmt(c.callSellStrike)}/${fmt(c.callBuyStrike)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                              ${fmt(c.netCredit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                              ${fmt(c.totalPremium)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              ${fmt(c.maxLoss)}
                            </TableCell>
                            <TableCell align="right">{fmtPct(c.returnOnRisk)}</TableCell>
                            <TableCell align="right">{fmtPct(c.probProfit)}</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="right">${fmt(c.totalShareCost || c.totalCashSecured || 0, 0)}</TableCell>
                            <TableCell align="right">${fmt(c.strike)}</TableCell>
                            <TableCell align="right">${fmt(c.mid)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                              ${fmt(c.totalPremium)}
                            </TableCell>
                            <TableCell align="right">{fmtPct(c.premiumYieldWeekly)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 500 }}>
                              {fmtPct(c.premiumYieldAnnual)}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">{fmtPct(c.impliedVolatility)}</TableCell>
                        <TableCell align="right">{fmt(c.delta, 3)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={fmt(c.score, 1)}
                            size="small"
                            color={c.score >= 30 ? 'success' : c.score >= 15 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayArrow />}
                            onClick={() => {
                              if (strategy === 'IRON_CONDOR') {
                                navigate(
                                  `/strategy/covered-call/new?symbol=${c.symbol}&expiration=${c.expiration}&contracts=${c.maxContracts}&strategy=IRON_CONDOR&putBuyStrike=${c.putBuyStrike}&putSellStrike=${c.putSellStrike}&callSellStrike=${c.callSellStrike}&callBuyStrike=${c.callBuyStrike}&strike=${c.callSellStrike}`
                                );
                              } else {
                                navigate(
                                  `/strategy/covered-call/new?symbol=${c.symbol}&strike=${c.strike}&expiration=${c.expiration}&contracts=${c.maxContracts}&strategy=${strategy}`
                                );
                              }
                            }}
                          >
                            Start
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Research;
